import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { API_BASE } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [pendingChallenge, setPendingChallenge] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check; AuthCallback handles it.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    refreshMe();
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) {
      setPendingChallenge({ challenge_id: data.challenge_id, email_hint: data.email_hint, methods: data.methods });
      return { mfa: true };
    }
    setPendingChallenge(null);
    // Refetch /auth/me so user.tenant + user.subscription (modules, plan) hydrate
    // immediately. Without this, the sidebar briefly renders "—" and all-LOCK
    // until the next page reload.
    const me = await refreshMe();
    return { mfa: false, user: me || data.user };
  };

  const verifyMfa = async (code, method) => {
    if (!pendingChallenge) throw new Error("No active challenge");
    const { data } = await api.post("/auth/mfa/verify", {
      challenge_id: pendingChallenge.challenge_id,
      code,
      method,
    });
    setPendingChallenge(null);
    const me = await refreshMe();
    return me || data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // If this user authenticated via SAML and the IdP advertises SLO, redirect to SP-initiated SLO instead of local logout.
    const sloUrl = user?.saml_slo_login_url;
    if (sloUrl && typeof user === "object") {
      window.location.href = `${API_BASE}${sloUrl}`;
      return;
    }
    try {
      await api.post("/auth/logout");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("auth.logout: server logout call failed (clearing local state anyway)", err);
    }
    setUser(false);
    setPendingChallenge(null);
  };

  const value = useMemo(
    () => ({ user, pendingChallenge, login, verifyMfa, register, logout, refreshMe, setPendingChallenge }),
    [user, pendingChallenge, refreshMe]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
